import { useState } from "react";
import { mutate } from "swr";
import useToast from "~/components/ui/toast";
import { useFetcher } from "~/hooks/useFetcher";
import {
  PinnedArtifact,
  PinArtifactInput,
  pinArtifact,
  projectPinnedArtifactsPath,
  unpinArtifact,
} from "~/services/pinnedArtifacts";
import { isApiError } from "~/utils/apiClient";

/** A project's pinned artifacts, oldest pinned first. Every member sees the
 *  same cards, so this is not narrowed to the caller. */
export const usePinnedArtifacts = (projectId: string) => {
  const { data, error, isLoading } = useFetcher<PinnedArtifact[]>(
    projectPinnedArtifactsPath(projectId)
  );
  return { pinnedArtifacts: data, error, isLoading };
};

export interface UsePinnedArtifactMutations {
  isMutating: boolean;
  // Pins an artifact, returning the created card or null on failure.
  pin: (input: PinArtifactInput) => Promise<PinnedArtifact | null>;
  unpin: (pin: PinnedArtifact) => Promise<void>;
}

// Curate-the-overview mutations, shared by the composer-side pin action and the
// card's own remove action so both agree on how the cache is patched.
export const usePinnedArtifactMutations = (projectId: string): UsePinnedArtifactMutations => {
  const { showError, showSuccess } = useToast();
  const [isMutating, setIsMutating] = useState(false);
  const key = projectPinnedArtifactsPath(projectId);

  const pin = async (input: PinArtifactInput) => {
    setIsMutating(true);
    try {
      const created = await pinArtifact(projectId, input);
      // No optimistic insert: the workflow is derived server-side, so there is
      // nothing truthful to show until the response carries it.
      void mutate(key, (current: PinnedArtifact[] | undefined) => [...(current ?? []), created], {
        revalidate: false,
      });
      showSuccess("Pinned to the project overview.");
      return created;
    } catch (e) {
      // 409 is the backend refusing to replace an existing pin's frozen recipe,
      // which is a thing the user can act on rather than a failure.
      showError(
        isApiError(e) && e.status === 409
          ? "That artifact is already pinned to this project."
          : "Couldn't pin this artifact"
      );
      return null;
    } finally {
      setIsMutating(false);
    }
  };

  const unpin = async (pin: PinnedArtifact) => {
    setIsMutating(true);
    void mutate(
      key,
      (current: PinnedArtifact[] | undefined) => current?.filter((p) => p.id !== pin.id),
      {
        revalidate: false,
      }
    );
    try {
      await unpinArtifact(projectId, pin.id);
      showSuccess("Removed from the project overview.");
    } catch {
      void mutate(
        key,
        (current: PinnedArtifact[] | undefined) => (current ? [...current, pin] : [pin]),
        {
          revalidate: false,
        }
      );
      showError("Couldn't unpin this artifact");
    } finally {
      setIsMutating(false);
    }
  };

  return { isMutating, pin, unpin };
};
